import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

const CartOpenContext = createContext(null)

export const CartOpenContextProvider = ({ children }) => {
  const [cartOpen, setCartOpen] = useState(false)
  const [cartDetails, setCartDetails] = useState({})
  const [coupon, setCoupon] = useState(null)

  // LocalStorage laden
  useEffect(() => {
    const stored = localStorage.getItem("cartStorage")
    if (stored) {
      try {
        setCartDetails(JSON.parse(stored))
      } catch (e) {
        console.error("cartStorage konnte nicht geladen werden", e)
      }
    }
  }, [])

  // LocalStorage persistieren
  useEffect(() => {
    localStorage.setItem("cartStorage", JSON.stringify(cartDetails))
  }, [cartDetails])

  // Alle Preisberechnungen als abgeleiteter Wert — kein separater State nötig
  const cartCalc = useMemo(
    () => calcCart(cartDetails, coupon),
    [cartDetails, coupon]
  )
  const cartCount = Object.keys(cartDetails).length

  return (
    <CartOpenContext.Provider
      value={{
        cartOpen,
        setCartOpen,
        cartDetails,
        setCartDetails,
        cartCount,
        cartCalc,
        coupon,
        setCoupon,
      }}
    >
      {children}
    </CartOpenContext.Provider>
  )
}

export const useCartOpen = () => {
  const {
    cartOpen,
    setCartOpen,
    cartDetails,
    setCartDetails,
    cartCount,
    cartCalc,
    coupon,
    setCoupon,
  } = useContext(CartOpenContext)

  const addItem = (value) => {
    const item = {
      ...value,
      formattedPrice: `${(value.price / 100).toLocaleString("de", {
        minimumFractionDigits: 2,
      })} €`,
      value: value.price,
      quantity: 1,
    }
    setCartDetails((prev) => ({ ...prev, [value.id]: item }))
  }

  const removeItem = (id) => {
    setCartDetails((prev) => {
      const copy = { ...prev }
      delete copy[id]
      return copy
    })
  }

  const clearCart = () => setCartDetails({})

  const addCoupon = (newCoupon) => setCoupon(newCoupon)
  const removeCoupon = () => setCoupon(null)

  return {
    cartOpen,
    setOpenCart: (value) => setCartOpen(value),
    cartDetails,
    addItem,
    cartCount,
    removeItem,
    cartCalc,
    coupon,
    addCoupon,
    removeCoupon,
    clearCart,
    // Abwärtskompatibilität für bestehende Stellen
    totalPrice: cartCalc.rawCents,
    formattedTotalPrice: cartCalc.rawFormatted,
  }
}

// Bundle-Rabatt: fixer Betrag in Cent
const BUNDLE_DISCOUNT_CENTS = 200 // 2,00 €

const calcBundleDiscount = (cart) => {
  const categories = cart.map((item) => item.category)
  if (
    categories.includes("Digitale Kursmappe") &&
    categories.includes("Digitaler Lehrerkalender")
  ) {
    return {
      name: "Bundle-Rabatt",
      type: "fixed",
      category: "bundle",
      valueCents: BUNDLE_DISCOUNT_CENTS,
    }
  }
  return null
}

// coupon: { name, type: "percent", rate: 0.2 }  (rate = 0–1)
const calcCart = (cartDetails, coupon = null) => {
  const cart = Object.values(cartDetails)

  // Rohbetrag in Cent (ganze Zahlen, kein Floating-Point-Problem)
  const rawCents = cart.reduce((sum, item) => sum + Number(item.value), 0)

  const discounts = []

  // 1. Bundle-Rabatt (fest)
  const bundleDiscount = calcBundleDiscount(cart)
  if (bundleDiscount) discounts.push(bundleDiscount)

  // 2. Rabattcode (prozentual, wird auf Betrag nach Bundle-Rabatt angewendet)
  if (coupon && coupon.type === "percent" && coupon.rate > 0) {
    const afterBundleCents =
      rawCents - (bundleDiscount ? bundleDiscount.valueCents : 0)
    const couponCents = Math.floor(afterBundleCents * coupon.rate)
    discounts.push({
      name: coupon.name,
      type: "percent",
      category: "coupon",
      valueCents: couponCents,
    })
  }

  const totalDiscountCents = discounts.reduce((sum, d) => sum + d.valueCents, 0)
  const finalCents = rawCents - totalDiscountCents

  const fmt = (cents) =>
    `${(cents / 100).toLocaleString("de", { minimumFractionDigits: 2 })} €`

  return {
    rawCents,
    rawFormatted: fmt(rawCents),
    finalCents,
    finalFormatted: fmt(Math.max(0, finalCents)),
    discounts: discounts.map((d) => ({
      ...d,
      formatted: `- ${fmt(d.valueCents)}`,
    })),
    totalDiscountCents,
  }
}
